/**
 * Shared helpers for Support at Home insight copy and “near maximum” Age Pension logic.
 * Keeps illness‑separated max (2 × single) aligned with calculateFinancials.
 */
import {
    PENSION_RATES,
    ASSET_TEST_LIMITS,
    INCOME_TEST_LOWER_LIMITS,
    ANNUAL_REDUCTION_PER_1000_ASSETS,
    INCOME_REDUCTION_RATE,
} from '../../constants/index.ts';
import { isIllnessSeparatedCouple, getAgedCareModellerResidencyOverrides } from '../../../../shared/src/calculation/householdStatus.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';

/** Row shape of `financials.socialSecurityBreakdown` (server/src/rest/calculation/assessment/financialCalculations.ts, itself untyped `any[]` there). */
export interface BreakdownRow {
    label: string;
    value: string;
}

/** Calc-engine `financials` result bag — free-form (see calculationApi.ts's own `Financials = Record<string, unknown>`). */
type FinancialsWithBreakdown = { socialSecurityBreakdown?: BreakdownRow[] } & Record<string, unknown>;

export function parseFormattedCurrencyToNumber(value: unknown): number | null {
    if (value == null || value === '') return null;
    const n = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : null;
}

/** True when Age Pension breakdown shows illness‑separated couple treatment. */
export function getIllnessSeparatedFromBreakdown(breakdown: BreakdownRow[] | undefined): boolean {
    if (!Array.isArray(breakdown)) return false;
    const row = breakdown.find((b) => b.label === 'Couple treatment');
    return row?.value === 'Illness separated';
}

/**
 * Illness-separated flag for SAH insight.
 *
 * The modelled financials are AUTHORITATIVE when present: the engine emits a "Couple treatment" row
 * for every couple (assessment/agePension.ts), and what it says is what every pension figure on the
 * page was computed with. The aged-care-modeller fallback exists only for the moment before
 * financials arrive — and it may run ONLY when an aged care scenario is actually linked on this
 * page. Without that gate, a couple both living at home was shown illness-separated targets
 * (2 × single ≈ $62k "full pension") merely because an aged care scenario EXISTED elsewhere in
 * their file: the modeller's overrides assume someone is entering residential care, which is that
 * module's premise, not this one's.
 */
export function resolveIllnessSeparatedForSahInsight(
    factFind: DraftFactFind,
    beforeFinancials: FinancialsWithBreakdown | undefined,
    afterFinancials: FinancialsWithBreakdown | undefined,
    agedCareScenarioLinked = false,
): boolean {
    if (factFind?.adviceFor !== 'couple') return false;
    const afterBreakdown = afterFinancials?.socialSecurityBreakdown;
    const beforeBreakdown = beforeFinancials?.socialSecurityBreakdown;
    if (Array.isArray(afterBreakdown) || Array.isArray(beforeBreakdown)) {
        return getIllnessSeparatedFromBreakdown(afterBreakdown) || getIllnessSeparatedFromBreakdown(beforeBreakdown);
    }
    if (!agedCareScenarioLinked) return false;
    // DraftFactFind is structurally compatible with FactFind (see
    // types/formDraft.ts); the shared calc-engine fns defensively coerce internally regardless,
    // so this cast is safe (see factFindTypes.ts header comment).
    const strictFactFind = factFind as unknown as FactFind;
    return Boolean(isIllnessSeparatedCouple(strictFactFind, getAgedCareModellerResidencyOverrides(strictFactFind)));
}

/** Combined maximum annual Age Pension rate used in comparisons (matches calculateFinancials pension cap). */
export function getMaxCombinedAgePensionRate(isCouple: boolean, illnessSeparated: boolean): number {
    if (!isCouple) return PENSION_RATES.single;
    if (illnessSeparated) return PENSION_RATES.single * 2;
    return PENSION_RATES.couple;
}

interface PensionUnderTests {
    assetTestPension: number;
    incomeTestPension: number;
    determinedBy: string | null;
}

function extractPensionUnderTests(breakdown: BreakdownRow[] | undefined): PensionUnderTests | null {
    if (!Array.isArray(breakdown)) return null;
    const under = breakdown.filter((b) => b.label === 'Pension under this test');
    if (under.length < 2) return null;
    const assetTestPension = parseFormattedCurrencyToNumber(under[0].value);
    const incomeTestPension = parseFormattedCurrencyToNumber(under[1].value);
    if (assetTestPension == null || incomeTestPension == null) return null;
    const determinedBy = breakdown.find((b) => b.label === 'Determined By')?.value ?? null;
    return { assetTestPension, incomeTestPension, determinedBy };
}

export type PensionNearCeilingVariant = 'atTrueMaximum' | 'incomeTestLimited' | 'assetTestLimited' | 'genericNearMax';

/**
 * Drives Strategy Impact Summary copy when pension is near the applicable combined maximum.
 */
export function classifyPensionNearCeilingVariant(breakdown: BreakdownRow[] | undefined, maxCombinedRate: number | null | undefined): PensionNearCeilingVariant {
    const derived = extractPensionUnderTests(breakdown);
    if (!derived || !maxCombinedRate || maxCombinedRate <= 0) return 'genericNearMax';
    const { assetTestPension, incomeTestPension, determinedBy } = derived;
    const tol = Math.max(150, maxCombinedRate * 0.002);
    if (Math.abs(assetTestPension - incomeTestPension) <= tol) return 'atTrueMaximum';
    if (determinedBy === 'Income Test' && assetTestPension > incomeTestPension + tol) return 'incomeTestLimited';
    if (determinedBy === 'Asset Test' && incomeTestPension > assetTestPension + tol) return 'assetTestLimited';
    return 'genericNearMax';
}


/** One indicative restructure target (pure data - presentation adds icons/colours). */
export interface SahOptimizationTarget {
    label: string;
    kind: 'full' | 'increase';
    targetPension: number;
    reductionNeeded: number | null;
    achievable: boolean;
    currentPension: number;
    increase: number;
    note?: string;
}

export interface SahOptimizationData {
    currentPension: number;
    currentAssets: number;
    currentIncome: number;
    determiningTest: 'asset' | 'income';
    isAssetTestLimiting: boolean;
    pensionFromAssetTest: number;
    pensionFromIncomeTest: number;
    targets: SahOptimizationTarget[];
    assetLowerLimit: number;
    assetUpperLimit: number;
}

/**
 * The Age Pension "room to move" analysis: which test binds, and the indicative asset-reduction
 * targets (full pension / +25% / +50% / +75%). Extracted from SupportAtHomeInsightPanel
 * (2026-08-25) so the panel and the inline starting-position card share ONE computation - the
 * frame rule: two surfaces quoting one calculation cannot disagree.
 */
export function calculateSahOptimizationTargets(input: {
    assessableAssets: number;
    assessableIncome: number;
    currentPension: number;
    isCouple: boolean;
    isHomeowner: boolean;
    illnessSeparatedForPension: boolean;
}): SahOptimizationData {
    const { assessableAssets, assessableIncome, currentPension, isCouple, isHomeowner, illnessSeparatedForPension } = input;
    const relationshipStatus: 'couple' | 'single' = isCouple ? 'couple' : 'single';
    const pensionRate = isCouple && illnessSeparatedForPension ? PENSION_RATES.single * 2 : PENSION_RATES[relationshipStatus];

    // Asset test thresholds (illness-separated: higher full/nil bands)
    const assetLimits = !isCouple
        ? (isHomeowner ? ASSET_TEST_LIMITS.single.homeowner : ASSET_TEST_LIMITS.single.nonHomeowner)
        : illnessSeparatedForPension
            ? (isHomeowner ? ASSET_TEST_LIMITS.coupleIllnessSeparated.homeowner : ASSET_TEST_LIMITS.coupleIllnessSeparated.nonHomeowner)
            : (isHomeowner ? ASSET_TEST_LIMITS.couple.homeowner : ASSET_TEST_LIMITS.couple.nonHomeowner);
    const assetLowerLimit = assetLimits.full;
    const assetUpperLimit = assetLimits.cutoff;

    let pensionFromAssetTest = pensionRate;
    if (assessableAssets > assetUpperLimit) {
        pensionFromAssetTest = 0;
    } else if (assessableAssets > assetLowerLimit) {
        const excessAssets = assessableAssets - assetLowerLimit;
        const reduction = (excessAssets / 1000) * ANNUAL_REDUCTION_PER_1000_ASSETS;
        pensionFromAssetTest = Math.max(0, pensionRate - reduction);
    }

    const incomeLowerLimit = INCOME_TEST_LOWER_LIMITS[relationshipStatus];
    let pensionFromIncomeTest = pensionRate;
    if (assessableIncome > incomeLowerLimit) {
        const excessIncome = assessableIncome - incomeLowerLimit;
        const reduction = excessIncome * INCOME_REDUCTION_RATE;
        pensionFromIncomeTest = Math.max(0, pensionRate - reduction);
    }

    const determiningTest: 'asset' | 'income' = pensionFromAssetTest < pensionFromIncomeTest ? 'asset' : 'income';
    const isAssetTestLimiting = determiningTest === 'asset';

    const targets: SahOptimizationTarget[] = [];
    if (isAssetTestLimiting) {
        const reductionForFullPension = Math.max(0, assessableAssets - assetLowerLimit);
        const fullPensionAchievable = reductionForFullPension <= assessableAssets;
        targets.push({
            label: 'Full Pension',
            kind: 'full',
            targetPension: pensionRate,
            reductionNeeded: reductionForFullPension,
            achievable: fullPensionAchievable,
            currentPension,
            increase: pensionRate - currentPension,
        });
        [0.25, 0.5, 0.75].forEach((percent) => {
            const targetPension = Math.min(pensionRate, currentPension * (1 + percent));
            const pensionIncrease = targetPension - currentPension;
            // Work backwards: assets = lowerLimit + ((pensionRate - pension) / taper) * 1000
            const requiredAssets = assetLowerLimit + ((pensionRate - targetPension) / ANNUAL_REDUCTION_PER_1000_ASSETS) * 1000;
            const reductionNeeded = Math.max(0, assessableAssets - requiredAssets);
            const achievable = reductionNeeded <= assessableAssets && targetPension <= pensionRate;
            targets.push({
                label: `Increase by ${Math.round(percent * 100)}%`,
                kind: 'increase',
                targetPension,
                reductionNeeded,
                achievable,
                currentPension,
                increase: pensionIncrease,
            });
        });
    } else {
        targets.push({
            label: 'Full Pension',
            kind: 'full',
            targetPension: pensionRate,
            reductionNeeded: null,
            achievable: false,
            currentPension,
            increase: pensionRate - currentPension,
            note: 'Income test is limiting - asset restructuring will not increase Age Pension',
        });
    }

    return {
        currentPension,
        currentAssets: assessableAssets,
        currentIncome: assessableIncome,
        determiningTest,
        isAssetTestLimiting,
        pensionFromAssetTest,
        pensionFromIncomeTest,
        targets,
        assetLowerLimit,
        assetUpperLimit,
    };
}
